import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  HostListener,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { NotificationService } from '../../../services/notification.service'; // NotificationService;
import { Subscription, Subject, takeUntil } from 'rxjs';
import { usaPhoneValidator } from '../../../shared/validators/phone.validator';
import { strictEmailValidator } from '../../../shared/validators/email.validator';
import { standardNameValidator } from '../../../shared/validators/name.validator';
import {
  CUSTOMER_PHONE_MAX_LENGTH,
  sanitizeCustomerPhoneInput,
} from '../../../shared/validators/customer-contact-field.rules';
import { LocationService } from '../../../services/location.service';
import { EmployeeService, EmployeeData } from '../../services/employee.service';
import { CompanyAdminService } from '../../services/company-admin.service';
import { LocationOption } from '../../../shared/types';
import { formatCalendarDateInTimeZone, formatDateToISO } from '../../../shared/utils/date.util';
import { Employee, UpdateEmployeeData } from '../../models/employee.models';
import {
  SuccessModalComponent,
  SuccessModalData,
} from '../../../shared/components/success-modal/success-modal.component';
import { CustomDatePickerComponent } from '../../../components/custom-date-picker/custom-date-picker.component';
import { AlertModalComponent } from '../../../shared/components/alert-modal/alert-modal.component';
import { environment } from '../../../../environments/environment';
import { CurrencyActionInputComponent } from '../../../shared/components/currency-action-input/currency-action-input.component';

const EMPLOYEE_FIELD_DISPLAY_NAMES: Record<string, string> = {
  first_name: 'First Name',
  last_name: 'Last Name',
  email: 'Email',
  phone_number: 'Phone Number',
  default_opening_balance: 'Opening Balance',
  username: 'Username',
  password: 'Password',
  designation: 'Role/Designation',
  role: 'Permission',
  location_id: 'Location',
  start_date: 'Start Date',
  emp_status: 'Status',
  status: 'Status',
};
export interface AddEmployeeFormData {
  employeeId: string;
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  default_opening_balance: number;
  username: string;
  password: string;
  designation: string;
  /** users.role — admin | attendant | attendant_plus | staff */
  role: 'admin' | 'attendant' | 'attendant_plus' | 'staff' | '';
  start_date: string;
  link: string;
  location_id: string;
  picture_url?: File;
}

/** CWS-1070: copy for the toast that replaced the edit success dialog. */
export const EDIT_SUCCESS_TOAST_DETAIL = 'Edited successfully';

@Component({
  selector: 'app-add-employee-modal',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatSelectModule,
    MatDatepickerModule,
    SuccessModalComponent,
    CustomDatePickerComponent,
    AlertModalComponent,
    CurrencyActionInputComponent,
  ],
  templateUrl: './add-employee-modal.component.html',
  styleUrls: ['./add-employee-modal.component.scss'],
})
export class AddEmployeeModalComponent implements OnInit, OnDestroy, OnChanges {
  @Input() isVisible = false;
  @Input() employee: Employee | null = null;
  @Output() closeModal = new EventEmitter<void>();
  @Output() saveEmployee = new EventEmitter<AddEmployeeFormData>();

  employeeForm!: FormGroup;
  profilePhotoFile: File | null = null;
  profilePhotoPreview: string | null = null;
  isSubmitting = false;
  readonly phoneMaxLength = CUSTOMER_PHONE_MAX_LENGTH;
  isUploadingPhoto = false;
  apiErrorMessage = '';
  uploadError: string | null = null;

  // Success modal state
  showSuccessModal = false;
  successModalData: SuccessModalData = {
    title: 'Employee Details Updated!',
    description: 'The information for this employee has been updated successfully.',
    buttonText: 'Okay',
    showTimeInfo: false,
  };

  // Close confirmation modal state
  showCloseConfirmationModal = false;

  // Location data from API
  locations: LocationOption[] = [];
  isLoadingLocations = false;
  private destroy$ = new Subject<void>();
  private locationSubscription?: Subscription;

  // Form change detection
  private originalFormValues: any = null;
  private originalPhotoPreview: string | null = null;
  /** Skip location→start-date side effects while patching the edit form. */
  private isPopulatingEmployeeForm = false;

  statuses = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
  ];

  /** Permission = users.role (not Role/Designation job title). Blank until selected. */
  permissionOptions = [
    { value: 'admin', label: 'Admin' },
    { value: 'attendant', label: 'Attendant' },
    { value: 'attendant_plus', label: 'Attendant plus' },
    { value: 'staff', label: 'Staff' },
  ];

  get isEditMode(): boolean {
    return !!this.employee;
  }

  /** Dynamic modal title based on create/edit and selected permission. */
  get modalTitle(): string {
    const role = this.employeeForm?.get('role')?.value ?? this.employee?.role;
    const isAdmin = role === 'admin';
    if (this.isEditMode) {
      return isAdmin ? 'Edit Admin' : 'Edit Employee';
    }
    return isAdmin ? 'Add Admin' : 'Add Employee';
  }

  get isAdminRole(): boolean {
    return this.employeeForm?.get('role')?.value === 'admin';
  }

  /** Opening balance is shop-floor only (hidden for admin and staff). */
  get showOpeningBalanceField(): boolean {
    const role = this.employeeForm?.get('role')?.value;
    return role === 'attendant' || role === 'attendant_plus';
  }

  /**
   * Employee-only fields (photo, emp id, designation, location, start date, invite link).
   * Hidden for admin — those are not stored on company-admin users.
   */
  get showEmployeeOpsFields(): boolean {
    return !this.isAdminRole;
  }

  /** Username is employee login; admin signs in with email only. */
  get showUsernameField(): boolean {
    return !this.isAdminRole;
  }

  /** Status on create for employees; for admin only on edit (update API). */
  get showStatusField(): boolean {
    return this.isEditMode || !this.isAdminRole;
  }

  /**
   * Get uppercase initials from the employee form's first and last name.
   * @example
   * this.employeeInitials()
   * 'JD'
   * @returns {{string}} Uppercase initials from first and last name, or an empty string if neither name is provided.
   **/
  get employeeInitials(): string {
    const firstName = this.employeeForm?.get('first_name')?.value || '';
    const lastName = this.employeeForm?.get('last_name')?.value || '';

    if (firstName && lastName) {
      return (firstName.charAt(0) + lastName.charAt(0)).toUpperCase();
    } else if (firstName) {
      return firstName.charAt(0).toUpperCase();
    } else if (lastName) {
      return lastName.charAt(0).toUpperCase();
    }
    return '';
  }

  /**
   * Determine whether the employee form or profile photo has unsaved changes.
   * @example
   * hasFormChanges()
   * true
   * @returns {boolean} True if the form is in create mode or any field/profile photo differs from original values; otherwise false.
   **/
  get hasFormChanges(): boolean {
    if (!this.isEditMode || !this.originalFormValues) {
      return true; // For create mode, always allow saving
    }

    const currentValues = this.employeeForm.getRawValue();

    // Check if any form field has changed
    for (const key in this.originalFormValues) {
      if (Object.hasOwn(this.originalFormValues, key)) {
        const originalValue = this.originalFormValues[key];
        const currentValue = currentValues[key];

        // Handle date comparison
        if (key === 'start_date') {
          const originalDate = originalValue ? new Date(originalValue).getTime() : null;
          const currentDate = currentValue ? new Date(currentValue).getTime() : null;
          if (originalDate !== currentDate) {
            return true;
          }
        } else if (originalValue !== currentValue) {
          return true;
        }
      }
    }

    // Check if profile photo has changed
    if (this.originalPhotoPreview !== this.profilePhotoPreview) {
      return true;
    }

    return false;
  }

  get isUpdateButtonDisabled(): boolean {
    if (!this.isEditMode) {
      // For create mode, use original validation
      return this.employeeForm.invalid || this.isSubmitting;
    }

    // For edit mode, check both validation and changes
    return this.employeeForm.invalid || this.isSubmitting || !this.hasFormChanges;
  }

  /**
   * Stable location timezone input for the start-date picker (updated only when location changes).
   * Avoids a template-bound getter that allocates a new object every change-detection cycle.
   */
  startDateLocationDetails: { timezone: string } | null = null;

  constructor(
    private fb: FormBuilder,
    private messageService: NotificationService,
    private locationService: LocationService,
    private employeeService: EmployeeService,
    private companyAdminService: CompanyAdminService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.initializeForm();
    this.setupStartDateLocationTimezone();
    this.setupRoleFieldState();
    this.loadLocations();
    this.setupFormChangeDetection();
  }

  /**
   * Handle changes to input properties to initialize or update the employee form when the modal visibility or employee data changes.
   * @example
   * gOnChanges({ isVisible: { currentValue: true }, employee: { currentValue: { id: 1, name: 'Jane' } } })
   * undefined
   * @param {{SimpleChanges}} {{changes}} - Object containing the changed input properties keyed by property name.
   * @returns {{void}} No return value.
   **/
  ngOnChanges(changes: SimpleChanges): void {
    // When modal becomes visible
    if (changes['isVisible'] && this.isVisible && this.employeeForm) {
      if (this.employee) {
        // Edit mode - populate with employee data
        this.populateFormForEdit();
      } else {
        // Create mode - reset form to fresh state
        this.resetForm();
      }
    }

    // When employee input changes
    if (changes['employee'] && this.employee && this.employeeForm) {
      this.populateFormForEdit();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.locationSubscription) {
      this.locationSubscription.unsubscribe();
    }
  }

  /**
   * Initialize the employeeForm FormGroup with controls, default values and validators.
   * @example
   * initializeForm()
   * undefined
   * @returns {void} Initializes this.employeeForm on the component instance; does not return a value.
   **/
  initializeForm() {
    this.employeeForm = this.fb.group({
      employeeId: ['', []], // Read-only field, auto-generated
      first_name: ['', [Validators.required, Validators.maxLength(100), standardNameValidator()]],
      last_name: ['', [Validators.required, Validators.maxLength(100), standardNameValidator()]],
      email: ['', [Validators.required, strictEmailValidator()]],
      phone_number: [
        '',
        [Validators.required, Validators.maxLength(CUSTOMER_PHONE_MAX_LENGTH), usaPhoneValidator()],
      ],
      default_opening_balance: [
        null,
        [Validators.required, Validators.min(1), Validators.max(1000000)],
      ],
      username: ['', [Validators.required, strictEmailValidator()]],
      password: ['', [Validators.required, Validators.minLength(8)]],
      designation: ['', [Validators.required]],
      role: ['', [Validators.required]],
      location_id: ['', [Validators.required]],
      start_date: ['', [Validators.required]],
      status: ['active', [Validators.required]], // Default to active
      link: [this.generateInviteLink(), []],
    });
  }

  /** Keep start-date calendar aligned with the selected location timezone. */
  private setupStartDateLocationTimezone(): void {
    this.updateStartDateLocationDetails();
    this.employeeForm
      .get('location_id')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateStartDateLocationDetails();
        // Create mode only: clear start date if it falls before the location's today.
        // Edit mode keeps the existing value; the picker still disables past days.
        if (!this.isPopulatingEmployeeForm && !this.isEditMode) {
          this.clearStartDateIfBeforeLocationToday();
        }
        this.cdr.markForCheck();
      });
  }

  /** Subscribe to permission changes and toggle field validators. */
  private setupRoleFieldState(): void {
    this.applyRoleFieldState();
    this.employeeForm
      .get('role')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.applyRoleFieldState();
        this.cdr.markForCheck();
      });
  }

  /**
   * Toggle validators for fields that are hidden/optional based on permission.
   * Admin: identity + password (+ status on edit); employee ops / username / opening balance cleared.
   * Staff: employee ops required; opening balance not required.
   * Attendant: employee ops + opening balance required.
   */
  private applyRoleFieldState(): void {
    if (!this.employeeForm) {
      return;
    }

    const role = this.employeeForm.get('role')?.value;
    const isAdmin = role === 'admin';
    const isShopFloor = role === 'attendant' || role === 'attendant_plus';

    const designation = this.employeeForm.get('designation');
    const locationId = this.employeeForm.get('location_id');
    const startDate = this.employeeForm.get('start_date');
    const openingBalance = this.employeeForm.get('default_opening_balance');
    const username = this.employeeForm.get('username');

    if (isAdmin) {
      designation?.clearValidators();
      locationId?.clearValidators();
      startDate?.clearValidators();
      openingBalance?.clearValidators();
      username?.clearValidators();
    } else {
      designation?.setValidators([Validators.required]);
      locationId?.setValidators([Validators.required]);
      startDate?.setValidators([Validators.required]);
      username?.setValidators([Validators.required, strictEmailValidator()]);
      if (isShopFloor) {
        openingBalance?.setValidators([
          Validators.required,
          Validators.min(1),
          Validators.max(1000000),
        ]);
      } else {
        openingBalance?.clearValidators();
      }
    }

    designation?.updateValueAndValidity({ emitEvent: false });
    locationId?.updateValueAndValidity({ emitEvent: false });
    startDate?.updateValueAndValidity({ emitEvent: false });
    openingBalance?.updateValueAndValidity({ emitEvent: false });
    username?.updateValueAndValidity({ emitEvent: false });
  }

  /** Enable permission on edit unless the current Permission is Admin. */
  private syncRoleControlDisabledState(): void {
    const roleControl = this.employeeForm?.get('role');
    if (!roleControl) {
      return;
    }
    const currentRole = this.employee?.role || roleControl.value;
    if (this.isEditMode && currentRole === 'admin') {
      roleControl.disable({ emitEvent: false });
    } else {
      roleControl.enable({ emitEvent: false });
    }
  }

  /** Cache timezone for the date picker; only replace the object when the timezone value changes. */
  private updateStartDateLocationDetails(): void {
    const locationId = this.employeeForm?.get('location_id')?.value;
    const timezone = locationId
      ? this.locationService.getLocationById(locationId)?.timezone?.trim()
      : undefined;
    const next = timezone ? { timezone } : null;
    if (this.startDateLocationDetails?.timezone === next?.timezone) {
      return;
    }
    this.startDateLocationDetails = next;
  }

  private clearStartDateIfBeforeLocationToday(): void {
    const startDateCtrl = this.employeeForm.get('start_date');
    const startDate = startDateCtrl?.value;
    const locationDetails = this.startDateLocationDetails;
    if (!startDate || !locationDetails?.timezone) {
      return;
    }
    const selectedYmd = formatCalendarDateInTimeZone(startDate, locationDetails.timezone);
    const locationTodayYmd = formatCalendarDateInTimeZone(new Date(), locationDetails.timezone);
    if (selectedYmd && locationTodayYmd && selectedYmd < locationTodayYmd) {
      startDateCtrl?.setValue('');
      startDateCtrl?.markAsUntouched();
    }
  }

  /**
   * Populate form with employee data for editing
   */
  private populateFormForEdit(): void {
    if (!this.employee) {
      console.log('No employee data to populate');
      return;
    }

    console.log('Populating form with employee data:', this.employee);
    console.log('Available locations:', this.locations);

    // Verify location exists in locations array
    const locationExists = this.locations.some(loc => loc.value === this.employee!.location_id);
    if (!locationExists && this.employee.location_id) {
      console.warn(
        '⚠️ Employee location_id not found in locations array:',
        this.employee.location_id
      );
      console.warn(
        '⚠️ Available location IDs:',
        this.locations.map(loc => loc.value)
      );
    }

    // Convert employee data to form format
    const formData = {
      employeeId: this.employee.emp_id || this.employee.id || '',
      first_name: this.employee.first_name || '',
      last_name: this.employee.last_name || '',
      email: this.employee.email || '',
      phone_number: sanitizeCustomerPhoneInput(this.employee.phone_number || ''),
      default_opening_balance: this.employee.default_opening_balance || null,
      username: this.employee.email || '', // Using email as username
      password: '', // Don't populate password for security
      designation: this.employee.designation || '',
      role: (this.employee.role as 'admin' | 'attendant' | 'attendant_plus' | 'staff' | '') || '',
      location_id: this.employee.location_id || '',
      start_date: this.employee.start_date ? new Date(this.employee.start_date) : '',
      status: this.employee.emp_status ? 'active' : 'inactive',
      link: this.employee.link || this.generateInviteLink(),
    };

    console.log('Form data to populate:', formData);
    console.log('Setting location_id to:', formData.location_id);

    // Set form values (suppress location→start-date side effects during populate)
    this.isPopulatingEmployeeForm = true;
    this.employeeForm.patchValue(formData);
    this.updateStartDateLocationDetails();
    this.isPopulatingEmployeeForm = false;

    // Set profile photo preview from the current employee (clear when none)
    if (this.employee.picture_url) {
      this.profilePhotoPreview = this.employee.picture_url;
    } else {
      this.profilePhotoPreview = null;
      this.profilePhotoFile = null;
    }

    // Make password optional for edit mode
    this.employeeForm.get('password')?.clearValidators();
    this.employeeForm.get('password')?.setValidators([]);
    this.employeeForm.get('password')?.updateValueAndValidity();

    this.syncRoleControlDisabledState();
    this.applyRoleFieldState();

    // Store original values for change detection
    this.originalFormValues = { ...formData };
    this.originalPhotoPreview = this.profilePhotoPreview;

    console.log('✅ Form populated successfully');
    console.log('Current form location_id value:', this.employeeForm.get('location_id')?.value);

    // Trigger change detection to update UI
    this.cdr.markForCheck();
  }

  /**
   * Setup form change detection to trigger change detection
   */
  private setupFormChangeDetection(): void {
    if (this.employeeForm) {
      this.employeeForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.cdr.detectChanges();
      });
    }
  }

  /**
   * Load locations from the API
   */
  private loadLocations(): void {
    this.isLoadingLocations = true;

    // Subscribe to locations from the service
    this.locationService.locations$.pipe(takeUntil(this.destroy$)).subscribe({
      next: locations => {
        this.locations = this.locationService.getLocationOptions();
        this.isLoadingLocations = false;
        this.updateStartDateLocationDetails();

        // If in edit mode and employee data exists, repopulate the form
        // This ensures location dropdown shows the selected value after locations are loaded
        if (this.isEditMode && this.employee && this.employeeForm) {
          // Use setTimeout to ensure locations are fully rendered before populating
          setTimeout(() => {
            this.populateFormForEdit();
          }, 0);
        }

        this.cdr.markForCheck();
      },
      error: error => {
        console.error('Error loading locations:', error);
        this.isLoadingLocations = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load locations. Please try again.',
          life: 3000,
        });
        this.cdr.markForCheck();
      },
    });

    // Load locations if not already loaded
    if (!this.locationService.hasLocations()) {
      this.locationService.loadLocations();
    }
  }

  /**
   * Close the add-employee modal: if not submitting, checks for form data and either shows a confirmation modal or emits the close event.
   * @example
   * nClose()
   * undefined
   * @returns {void} No value is returned.
   */
  onClose(): void {
    if (!this.isSubmitting) {
      // Check if form has any data
      const hasData = this.hasFormData();
      if (hasData) {
        this.showCloseConfirmationModal = true;
        this.cdr.markForCheck(); // Trigger change detection for OnPush strategy
      } else {
        this.closeModal.emit();
      }
    }
  }

  /**
   * Determine whether the employee form contains any user-entered data or modifications.
   * @example
   * hasFormData()
   * true
   * @returns {{boolean}} True if the form has data or changes; false otherwise.
   **/
  private hasFormData(): boolean {
    if (!this.employeeForm) {
      return false;
    }

    // For edit mode, check if form has been modified
    if (this.isEditMode) {
      return this.hasFormChanges;
    }

    // For create mode, check if any field has been filled or touched
    // First check if form has been touched (user has interacted with it)
    const formTouched = this.employeeForm.touched;

    const formValue = this.employeeForm.value;

    // Helper to check if a string value has actual content
    const hasValue = (val: any): boolean => {
      if (val === null || val === undefined) return false;
      if (typeof val === 'string') {
        return val.trim().length > 0;
      }
      return true;
    };

    // Check string fields (exclude empty strings and whitespace)
    const hasStringData =
      hasValue(formValue.first_name) ||
      hasValue(formValue.last_name) ||
      hasValue(formValue.email) ||
      hasValue(formValue.phone_number) ||
      hasValue(formValue.username) ||
      hasValue(formValue.password) ||
      hasValue(formValue.designation) ||
      hasValue(formValue.role);

    // Check other fields (location_id, start_date are required fields, so if set, user has entered data)
    const hasOtherData =
      hasValue(formValue.location_id) ||
      (formValue.start_date && formValue.start_date !== '') ||
      !!this.profilePhotoFile ||
      !!this.profilePhotoPreview;

    // Also check if any form control has been touched (user has interacted with the form)
    const hasTouchedFields = Object.keys(this.employeeForm.controls).some(key => {
      const control = this.employeeForm.get(key);
      return control ? control.touched : false;
    });

    return hasStringData || hasOtherData || (formTouched && hasTouchedFields);
  }

  onCloseConfirmationCancel(): void {
    this.showCloseConfirmationModal = false;
    this.cdr.markForCheck();
  }

  onCloseConfirmationConfirm(): void {
    this.showCloseConfirmationModal = false;
    this.cdr.markForCheck();
    this.closeModal.emit();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isVisible) return;
    this.handleDropdownClickEvent(event.target as HTMLElement);
  }

  onModalContentClick(event: MouseEvent): void {
    this.handleDropdownClickEvent(event.target as HTMLElement);
  }

  /** Shared click-routing logic for both document and modal-content click handlers. */
  private handleDropdownClickEvent(target: HTMLElement): void {
    if (target.closest('.p-dropdown-panel')) {
      return;
    }
    if (target.closest('.p-dropdown')) {
      setTimeout(() => {
        this.closeOtherDropdowns(target);
      }, 0);
      return;
    }
    this.closeAllDropdowns();
  }

  private closeAllDropdowns(): void {
    // Material selects handle dropdown closing automatically
    // No need to manually manage with Material components
  }

  /**
   * Close any open dropdowns except the dropdown that contains the specified target element.
   * @example
   * closeOtherDropdowns(targetElement)
   * undefined
   * @param {HTMLElement} target - The DOM element that was interacted with; the dropdown that contains this element will remain open.
   * @returns {void} No return value.
   */
  private closeOtherDropdowns(target: HTMLElement): void {
    // Material selects handle dropdowns automatically
    // No need to manually manage closing of other dropdowns
  }

  /**
   * Save the employee form: validate, then create or update an employee/admin via the API and handle UI state, messages, and validation errors.
   * @example
   * nSave()
   * undefined
   * @returns {void} No return value; triggers create/update API calls, displays success/error messages, and marks form controls as touched on validation failure.
   */
  onSave(): void {
    // Use getRawValue so disabled role (edit mode) is included.
    const formValue = this.employeeForm.getRawValue();
    if (this.employeeForm.valid && !this.isSubmitting) {
      this.isSubmitting = true;
      this.apiErrorMessage = '';

      if (formValue.role === 'admin') {
        this.saveCompanyAdmin(formValue);
        return;
      }

      // Format the date to ISO 8601 format
      const startDate = formatDateToISO(formValue.start_date);
      const isShopFloor = formValue.role === 'attendant' || formValue.role === 'attendant_plus';

      if (this.isEditMode && this.employee) {
        const updateData: UpdateEmployeeData = {
          first_name: formValue.first_name,
          last_name: formValue.last_name,
          email: formValue.email,
          phone_number: formValue.phone_number,
          designation: formValue.designation,
          role: formValue.role,
          location_id: formValue.location_id,
          start_date: startDate,
          emp_status: formValue.status === 'active',
          username: formValue.username,
          password: formValue.password || undefined,
          picture_url: this.profilePhotoFile || undefined,
        };
        if (isShopFloor) {
          updateData.default_opening_balance = formValue.default_opening_balance;
        }

        this.employeeService.updateEmployee(this.employee.id, updateData).subscribe({
          next: response => {
            this.isSubmitting = false;
            if (response.status && response.status_code === 200) {
              const permissionChanged =
                Boolean(this.employee?.role) &&
                formValue.role &&
                String(this.employee.role) !== String(formValue.role);
              // CWS-1070: a successful edit finishes on its own -- toast and close, rather
              // than parking a success dialog over the page for the user to dismiss. The
              // permission-change warning still has to be said, so it becomes the toast text.
              this.emitEditSuccess(
                formValue,
                permissionChanged
                  ? 'Permission updated. The employee must sign in again to see the updated navigation.'
                  : EDIT_SUCCESS_TOAST_DETAIL
              );
            } else {
              this.apiErrorMessage =
                response.message || 'Failed to update employee. Please try again.';

              if (response.errors && typeof response.errors === 'object') {
                this.handleFieldValidationErrors(response.errors);
                const detailedMessage = this.extractErrorMessage({ error: response });
                this.apiErrorMessage = detailedMessage || this.apiErrorMessage;
              }

              this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: this.apiErrorMessage,
                life: 5000,
              });
              this.cdr.markForCheck();
            }
          },
          error: error => {
            console.error('Error updating employee:', error);
            this.handleApiSubmitError(error);
          },
        });
      } else {
        const commonEmployeeFields: Omit<EmployeeData, 'emp_status' | 'picture_url'> & {
          picture_url?: File;
          employeeId?: string;
        } = {
          first_name: formValue.first_name,
          last_name: formValue.last_name,
          email: formValue.email,
          phone_number: formValue.phone_number,
          username: formValue.username,
          password: formValue.password,
          designation: formValue.designation,
          role: formValue.role,
          start_date: startDate,
          link: formValue.link,
          location_id: formValue.location_id,
          picture_url: this.profilePhotoFile || undefined,
        };
        if (isShopFloor) {
          commonEmployeeFields.default_opening_balance = formValue.default_opening_balance;
        }
        const employeeData: EmployeeData = {
          ...commonEmployeeFields,
          emp_status: formValue.status === 'active',
        };

        this.employeeService.createEmployee(employeeData).subscribe({
          next: response => {
            this.isSubmitting = false;
            if (response.status) {
              this.successModalData = {
                title: 'Employee Added!',
                description:
                  'A new employee entry has been created successfully. It can be updated later if needed.',
                buttonText: 'Okay',
                showTimeInfo: false,
              };
              this.showSuccessModal = true;
              const formData: AddEmployeeFormData = {
                ...commonEmployeeFields,
                default_opening_balance: commonEmployeeFields.default_opening_balance ?? 0,
                employeeId: '',
              };
              this.saveEmployee.emit(formData);
            } else {
              this.apiErrorMessage =
                response.message || 'Failed to create employee. Please try again.';

              const responseWithErrors = response as any;
              if (responseWithErrors.errors && typeof responseWithErrors.errors === 'object') {
                this.handleFieldValidationErrors(responseWithErrors.errors);
                const detailedMessage = this.extractErrorMessage({ error: responseWithErrors });
                this.apiErrorMessage = detailedMessage || this.apiErrorMessage;
              }

              this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: this.apiErrorMessage,
                life: 5000,
              });
              this.cdr.markForCheck();
            }
          },
          error: error => {
            console.error('Error creating employee:', error);
            this.handleApiSubmitError(error);
          },
        });
      }
    } else {
      Object.keys(this.employeeForm.controls).forEach(key => {
        this.employeeForm.get(key)?.markAsTouched();
      });

      if (this.employeeForm.invalid) {
        const invalidFields = this.getInvalidFields();
        const errorMessage =
          invalidFields.length > 0
            ? `Please fix the following errors: ${invalidFields.join(', ')}`
            : 'Please fill in all required fields correctly';

        this.messageService.add({
          severity: 'error',
          summary: 'Validation Error',
          detail: errorMessage,
          life: 3000,
        });
      }
      this.cdr.markForCheck();
    }
  }

  /** Create or update a company admin via CompanyAdminService. */
  private saveCompanyAdmin(formValue: any): void {
    const name = `${formValue.first_name || ''} ${formValue.last_name || ''}`.trim();
    const contact_number = formValue.phone_number || '';

    if (this.isEditMode && this.employee) {
      const adminId = this.employee.user_id || this.employee.id;
      const payload: {
        name: string;
        email: string;
        contact_number: string;
        password?: string;
        status?: boolean;
      } = {
        name,
        email: formValue.email,
        contact_number,
      };
      if (formValue.password) {
        payload.password = formValue.password;
      }
      if (formValue.status !== undefined && formValue.status !== null && formValue.status !== '') {
        payload.status = formValue.status === 'active';
      }

      this.companyAdminService.updateCompanyAdmin(adminId, payload).subscribe({
        next: response => {
          this.isSubmitting = false;
          if (response?.status) {
            // CWS-1070: same as the employee edit above -- toast, then close.
            this.emitEditSuccess(formValue, EDIT_SUCCESS_TOAST_DETAIL);
          } else {
            this.apiErrorMessage = response?.message || 'Failed to update admin. Please try again.';
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: this.apiErrorMessage,
              life: 5000,
            });
            this.cdr.markForCheck();
          }
        },
        error: error => {
          console.error('Error updating company admin:', error);
          this.handleApiSubmitError(error);
        },
      });
      return;
    }

    this.companyAdminService
      .inviteCompanyAdmin({
        name,
        email: formValue.email,
        contact_number,
        password: formValue.password,
      })
      .subscribe({
        next: response => {
          this.isSubmitting = false;
          if (response?.status) {
            this.successModalData = {
              title: 'Admin Added!',
              description: 'A new admin has been added successfully.',
              buttonText: 'Okay',
              showTimeInfo: false,
            };
            this.showSuccessModal = true;
            this.saveEmployee.emit(formValue);
          } else {
            this.apiErrorMessage = response?.message || 'Failed to invite admin. Please try again.';
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: this.apiErrorMessage,
              life: 5000,
            });
            this.cdr.markForCheck();
          }
        },
        error: error => {
          console.error('Error inviting company admin:', error);
          this.handleApiSubmitError(error);
        },
      });
  }

  /**
   * Handle file input selection: validate allowed image types (PNG/JPEG/JPG/GIF) and a 20MB size limit, clear previous errors, set upload state, store the selected file, trigger change detection, and create an image preview.
   * @example
   * nFileSelected(event)
   * void
   * @param {any} event - File input change event containing the selected file(s).
   * @returns {void} Does not return a value; updates component state and may set uploadError or start the upload/preview flow.
   **/
  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      this.uploadError = null; // Clear previous errors

      // Validate file type - specific formats like profile module
      const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif'];
      if (!allowedTypes.includes(file.type)) {
        this.uploadError = 'Unsupported file format. Please upload a JPG, PNG, or GIF image.';
        this.cdr.detectChanges();
        return;
      }

      // Validate file size (max 20MB like profile module)
      const maxSize = 20 * 1024 * 1024; // 20MB
      if (file.size > maxSize) {
        this.uploadError = 'File exceeds 20MB size limit.';
        this.cdr.detectChanges();
        return;
      }

      this.isUploadingPhoto = true;
      this.profilePhotoFile = file;
      this.cdr.detectChanges(); // Trigger change detection
      console.log('Starting photo upload, isUploadingPhoto:', this.isUploadingPhoto);
      this.createImagePreview(file);
    }
  }

  /**
   * Create a preview for an uploaded image file by reading it as a Data URL and updating component state.
   * @example
   * createImagePreview(file)
   * void
   * @param {{File}} {{file}} - File to read and generate a Data URL preview for.
   * @returns {{void}} No return value; updates component properties (profilePhotoPreview, isUploadingPhoto, uploadError) and triggers change detection.
   **/
  private createImagePreview(file: File): void {
    const reader = new FileReader();

    reader.onload = (e: any) => {
      this.profilePhotoPreview = e.target.result;
      this.isUploadingPhoto = false;
      this.cdr.detectChanges(); // Trigger change detection for photo change

      console.log('Photo upload completed, isUploadingPhoto:', this.isUploadingPhoto);
    };

    reader.onerror = () => {
      this.isUploadingPhoto = false;
      this.profilePhotoFile = null;
      this.profilePhotoPreview = null;
      this.uploadError = 'Error reading file. Please try again.';
      this.cdr.detectChanges(); // Trigger change detection
    };

    reader.readAsDataURL(file);
  }

  /**
   * Return a user-facing validation error message for a given form field if it has been touched and has errors, otherwise return an empty string.
   * @example
   * etFieldError('email')
   * 'Invalid email address.'
   * @param {string} fieldName - The name of the form control to check for validation errors.
   * @returns {string} A validation error message for the field or an empty string if there is no visible error.
   **/
  getFieldError(fieldName: string): string {
    const field = this.employeeForm.get(fieldName);

    if (field?.errors && (field.touched || field.dirty)) {
      this.cdr.markForCheck();
      // Check for server-side validation errors first
      if (field.errors['serverError']) {
        return field.errors['serverError'];
      }
      if (field.errors['required']) return `${this.getFieldDisplayName(fieldName)} is required`;
      if (field.errors['email']) return 'Invalid email address.';
      if (field.errors['strictEmail']) return 'Please enter a valid email address.';
      if (field.errors['usaPhone']) return field.errors['usaPhone'].message;
      if (field.errors['standardName']) return field.errors['standardName'].message;
      if (field.errors['minlength'])
        return `${this.getFieldDisplayName(fieldName)} must be at least ${field.errors['minlength'].requiredLength} characters`;
      if (field.errors['maxlength'])
        return `${this.getFieldDisplayName(fieldName)} cannot exceed ${field.errors['maxlength'].requiredLength} characters`;
    }
    return '';
  }

  /**
   * Convert an internal field name to a user-friendly display label.
   * @example
   * getFieldDisplayName('first_name')
   * 'First Name'
   * @param {string} fieldName - The internal field key to convert (e.g. 'first_name').
   * @returns {string} The user-friendly display name for the given field, or the original fieldName if no mapping exists.
   **/
  private getFieldDisplayName(fieldName: string): string {
    const displayNames: Record<string, string> = {
      employeeId: 'Employee ID',
      first_name: 'First Name',
      last_name: 'Last Name',
      email: 'Email',
      phone_number: 'Phone Number',
      default_opening_balance: 'Opening Balance',
      designation: 'Role/Designation',
      role: 'Permission',
      location_id: 'Location',
      start_date: 'Start Date',
      username: 'User Name',
      password: 'Password',
    };
    return displayNames[fieldName] || fieldName;
  }

  onPhoneKeyup() {
    const phoneControl = this.employeeForm.get('phone_number');
    if (phoneControl) {
      phoneControl.markAsTouched();
      phoneControl.updateValueAndValidity();
    }
  }

  /** Digits only, max 10 — same rules as Customers / Orders / Appointments. */
  onPhoneInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    const sanitized = sanitizeCustomerPhoneInput(target.value);
    if (target.value !== sanitized) {
      target.value = sanitized;
    }
    const control = this.employeeForm.get('phone_number');
    if (control && control.value !== sanitized) {
      control.setValue(sanitized, { emitEvent: true });
    }
  }

  /**
   * Generate a public-facing invite URL from the environment baseUrl by removing any trailing '/api' segment or the 'api.' subdomain prefix.
   * @example
   * generateInviteLink()
   * https://example.com
   * @returns {string} The cleaned invite URL without a trailing '/api' or 'api.' subdomain.
   */
  generateInviteLink(): string {
    const baseUrl = environment.baseUrl;

    // Step 1: Remove '/api' from the end
    let inviteUrl = baseUrl.endsWith('/api') ? baseUrl.slice(0, -4) : baseUrl;

    // Step 2: Remove 'api.' from the beginning
    inviteUrl = inviteUrl.startsWith('https://api.')
      ? inviteUrl.replace('https://api.', 'https://')
      : inviteUrl;

    return inviteUrl;
  }

  /**
   * Copy the invite link from the employee form to the clipboard and display a success message.
   * @example
   * opyInviteLink()
   * undefined
   * @returns {{void}} No return value.
   **/
  copyInviteLink() {
    const inviteLink = this.employeeForm.get('link')?.value;
    if (inviteLink) {
      navigator.clipboard.writeText(inviteLink).then(() => {
        this.messageService.add({
          severity: 'success',
          summary: 'Copied',
          detail: 'Invite link copied to clipboard',
          life: 2000,
        });
      });
    }
  }

  /**
   * Reset the employee creation form, clear server-side validation errors, reset profile photo state, and restore UI flags to their initial values.
   * @example
   * esetForm()
   * undefined
   * @returns {void} No return value.
   **/
  resetForm(): void {
    this.employeeForm.reset({
      link: this.generateInviteLink(),
      status: 'active',
    });
    this.updateStartDateLocationDetails();

    // Restore password required for create mode
    this.employeeForm
      .get('password')
      ?.setValidators([Validators.required, Validators.minLength(8)]);
    this.employeeForm.get('password')?.updateValueAndValidity({ emitEvent: false });

    this.syncRoleControlDisabledState();
    this.applyRoleFieldState();

    // Clear all server errors from form controls
    Object.keys(this.employeeForm.controls).forEach(key => {
      const control = this.employeeForm.get(key);
      if (control) {
        const errors = control.errors;
        if (errors && errors['serverError']) {
          delete errors['serverError'];
          if (Object.keys(errors).length === 0) {
            control.setErrors(null);
          } else {
            control.setErrors(errors);
          }
        }
        control.markAsUntouched();
      }
    });

    this.profilePhotoFile = null;
    this.profilePhotoPreview = null;
    this.isSubmitting = false;
    this.isUploadingPhoto = false;
    this.apiErrorMessage = '';
    this.uploadError = null;

    // Clear original values for change detection
    this.originalFormValues = null;
    this.originalPhotoPreview = null;

    this.cdr.markForCheck();
  }

  // Called from parent component to reset form state
  resetSubmittingState() {
    this.isSubmitting = false;
  }

  /**
   * Handle success modal close
   */
  onSuccessModalClose(): void {
    this.showSuccessModal = false;
    this.cdr.detectChanges(); // Trigger change detection for OnPush strategy
    this.resetForm();
    this.closeModal.emit();
  }

  /**
   * Refresh locations from API
   */
  refreshLocations(): void {
    this.locationService.refreshLocations();
  }

  /**
   * CWS-1070: finish a successful edit without a confirmation dialog.
   *
   * Toast the outcome, clear the form and tell the parent, which closes the modal and
   * reloads the list. Creating a user deliberately still uses the success dialog: the
   * ticket asks only for the edit flow and lists "no regression on create-user" as an
   * acceptance criterion.
   */
  private emitEditSuccess(formValue: AddEmployeeFormData, detail: string): void {
    this.messageService.add({
      severity: 'success',
      summary: 'Success',
      detail,
      life: 5000,
    });
    this.resetForm();
    this.saveEmployee.emit(formValue);
    this.cdr.markForCheck();
  }

  /** Shared HTTP error handler for both create and update employee calls. */
  private handleApiSubmitError(error: any): void {
    this.isSubmitting = false;
    const errorMessage = this.extractErrorMessage(error);
    this.apiErrorMessage = errorMessage;
    if (error.error?.errors && typeof error.error.errors === 'object') {
      this.handleFieldValidationErrors(error.error.errors);
    }
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: errorMessage,
      life: 5000,
    });
    this.cdr.markForCheck();
  }

  /**
   * Extract detailed error message from error object
   */
  private extractErrorMessage(error: any): string {
    // Check for field-specific errors first
    if (error.error?.errors && typeof error.error.errors === 'object') {
      const errorMessages: string[] = [];
      const errors = error.error.errors;

      // Map common field names
      // Collect all error messages
      for (const [field, message] of Object.entries(errors)) {
        const fieldName = EMPLOYEE_FIELD_DISPLAY_NAMES[field] || field;
        if (Array.isArray(message)) {
          errorMessages.push(`${fieldName}: ${message.join(', ')}`);
        } else if (typeof message === 'string') {
          errorMessages.push(`${fieldName}: ${message}`);
        }
      }

      if (errorMessages.length > 0) {
        return errorMessages.join('. ');
      }
    }

    // Check for direct error message
    if (error.error?.message) {
      return error.error.message;
    }

    // Handle specific HTTP status codes with detailed messages
    if (error.status === 409) {
      return (
        error.error?.message ||
        'An employee with this email or username already exists. Please use a different email.'
      );
    } else if (error.status === 400) {
      return (
        error.error?.message || 'Invalid data provided. Please check all fields and try again.'
      );
    } else if (error.status === 401) {
      return 'Unauthorized. Please login again.';
    } else if (error.status === 403) {
      return 'You do not have permission to perform this action.';
    } else if (error.status === 404) {
      return 'Employee not found. Please refresh and try again.';
    } else if (error.status === 422) {
      return error.error?.message || 'Validation failed. Please check the form fields.';
    } else if (error.status === 500) {
      return 'Server error occurred. Please try again later or contact support.';
    } else if (error.status === 0 || error.status === undefined) {
      return 'Network error. Please check your internet connection and try again.';
    } else {
      return (
        error.error?.message ||
        `Failed to ${this.isEditMode ? 'update' : 'create'} employee. Please try again.`
      );
    }
  }

  /**
   * Handle field-specific validation errors from API
   */
  private handleFieldValidationErrors(errors: any): void {
    const fieldMap: Record<string, string> = {
      first_name: 'first_name',
      last_name: 'last_name',
      email: 'email',
      phone_number: 'phone_number',
      default_opening_balance: 'default_opening_balance',
      username: 'username',
      password: 'password',
      designation: 'designation',
      role: 'role',
      location_id: 'location_id',
      start_date: 'start_date',
      emp_status: 'status',
    };

    // Set errors on form controls
    for (const [field, message] of Object.entries(errors)) {
      const formFieldName = fieldMap[field] || field;
      const control = this.employeeForm.get(formFieldName);

      if (control) {
        const errorMessage = Array.isArray(message) ? message[0] : message;
        control.setErrors({
          serverError: errorMessage,
        });
        control.markAsTouched();
      }
    }

    this.cdr.markForCheck();
  }

  /**
   * Get list of invalid field names for validation error message
   */
  private getInvalidFields(): string[] {
    const invalidFields: string[] = [];
    Object.keys(this.employeeForm.controls).forEach(key => {
      const control = this.employeeForm.get(key);
      if (control && control.invalid && control.touched) {
        const displayName = EMPLOYEE_FIELD_DISPLAY_NAMES[key] || key;
        invalidFields.push(displayName);
      }
    });

    return invalidFields;
  }

  /**
   * Keydown handler to prevent typing beyond 50 characters and show error
   */
  onDesignationKeydown(event: KeyboardEvent): void {
    const target = event.target as HTMLInputElement;
    const currentValue = target.value;
    const designationControl = this.employeeForm.get('designation');

    // Allow backspace, delete, arrow keys, tab, etc.
    const allowedKeys = [
      'Backspace',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Tab',
      'Home',
      'End',
    ];
    if (allowedKeys.includes(event.key)) {
      return;
    }

    // Allow Ctrl/Cmd combinations (Ctrl+A, Ctrl+C, etc.)
    if (event.ctrlKey || event.metaKey) {
      return;
    }

    // Check if current value is already at 50 characters
    if (currentValue.length >= 50) {
      // Prevent typing the 51st character
      event.preventDefault();

      // Set maxlength error
      designationControl?.setErrors({
        maxlength: {
          requiredLength: 50,
          actualLength: currentValue.length,
        },
      });
      designationControl?.markAsTouched();
      designationControl?.updateValueAndValidity();
      this.cdr.markForCheck();
    }
  }

  /**
   * Input handler for designation field to prevent special characters in real-time
   */
  onDesignationInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    const value = target.value;
    const designationControl = this.employeeForm.get('designation');

    // Only allow letters, spaces, and hyphens - remove any special characters
    let filteredValue = value.replace(/[^A-Za-z\s-]/g, '');

    // Limit to 50 characters (in case of paste or other input methods)
    if (filteredValue.length > 50) {
      filteredValue = filteredValue.substring(0, 50);
      target.value = filteredValue;
      designationControl?.setValue(filteredValue, { emitEvent: true });

      // Set maxlength error
      designationControl?.setErrors({
        maxlength: {
          requiredLength: 50,
          actualLength: filteredValue.length,
        },
      });
    } else {
      // Update the input value if filtering occurred
      if (value !== filteredValue) {
        target.value = filteredValue;
        designationControl?.setValue(filteredValue, { emitEvent: true });
      }

      // Clear maxlength error if within limit
      if (filteredValue.length < 50 && designationControl?.hasError('maxlength')) {
        const errors = designationControl.errors;
        if (errors) {
          delete errors['maxlength'];
          if (Object.keys(errors).length === 0) {
            designationControl.setErrors(null);
          } else {
            designationControl.setErrors(errors);
          }
        }
      }
    }

    // Always mark as touched to show validation errors
    designationControl?.markAsTouched();
    designationControl?.updateValueAndValidity();

    // Trigger change detection to update error message display
    this.cdr.markForCheck();
  }
}
